<template>
    <form id="eligibility-form" @submit.prevent="emitFormData">
        <h2>Vérification d'éligibilité</h2>

        <hr>

        <!-- ENTREPRISE -->
        <section class="grouped-inputs">
            <h3>Informations sur l'entreprise</h3>

            <div class="form-field">
                <label for="registered">
                    <span>Immatriculé</span>
                </label>
                <SwitchButton id="registered" style_type="discreet" v-model:checked="eligibilityForm.registered.value"></SwitchButton>
            </div>

            <div v-if="eligibilityForm.registered.value" class="form-field">
                <label for="registration_date">
                    <span>Date d'immatriculation&nbsp;<span class="important">*</span></span>
                </label>
                <input type="date" id="registration_date" v-model="eligibilityForm.registration_date.value" :class="{ 'input-error': showError('registration_date') }" @blur="touch('registration_date')">
            </div>

            <div v-if="eligibilityForm.registered.value" class="form-field">
                <label for="is_executive">
                    <span>Dirigeant ou co-dirigeant</span>
                </label>
                <SwitchButton id="is_executive" style_type="discreet" v-model:checked="eligibilityForm.is_executive.value"></SwitchButton>
            </div>

            <div v-if="eligibilityForm.registered.value" class="form-field">
                <label for="legal_status">
                    <span>Statut juridique&nbsp;<span class="important">*</span></span>
                </label>
                <select id="legal_status" v-model="eligibilityForm.legal_status.value" :class="{ 'input-error': showError('legal_status') }" @blur="touch('legal_status')">
                    <option :value="null" disabled>Choisir un statut</option>
                    <option value="micro">Micro-entreprise</option>
                    <option value="other">Autre</option>
                </select>
            </div>

            <div v-if="eligibilityForm.registered.value" class="form-field">
                <label for="annual_revenue">
                    <span>Chiffre d'affaires de l'année N-1</span>
                </label>
                <div class="input-wrapper">
                    <input type="number" id="annual_revenue" min="0" step="1000"
                        v-model.lazy="eligibilityForm.annual_revenue.value"
                        :class="{ 'input-error': showError('annual_revenue') }" @blur="touch('annual_revenue')">
                    <span class="input-suffix">€ / an</span>
                </div>
            </div>
        </section>

        <hr>

        <!-- PROFIL PERSONNEL -->
        <section class="grouped-inputs">
            <h3>Profil personnel</h3>

            <div class="form-field">
                <label for="birthday">
                    <span>Date de naissance
                        <span v-if="eligibilityForm.birthday.value" class="age-display">
                            ({{ computedAge }} ans)
                        </span>
                        &nbsp;<span class="important">*</span>
                    </span>
                </label>
                <input type="date" id="birthday" v-model="eligibilityForm.birthday.value" :class="{ 'input-error': showError('birthday') }" @blur="touch('birthday')">
            </div>

            <div class="form-field">
                <label for="gender">
                    <span>Civilité&nbsp;<span class="important">*</span></span>
                </label>
                <select id="gender" v-model="eligibilityForm.gender.value"
                    :class="{ 'input-error': showError('gender') }" @blur="touch('gender')">
                    <option :value="null" disabled>Choisir une civilité</option>
                    <option value="female">Féminin</option>
                    <option value="male">Masculin</option>
                    <option value="other">Autre</option>
                </select>
            </div>

            <div class="form-field">
                <label for="status">
                    <span>Situation professionnelle&nbsp;<span class="important">*</span></span>
                </label>
                <select id="status" v-model="eligibilityForm.status.value" :class="{ 'input-error': showError('status') }" @blur="touch('status')">
                    <option :value="null" disabled>Choisir une situation</option>
                    <option value="unemployed">Demandeur d'emploi</option>
                    <option value="employed_full">Salarié temps plein</option>
                    <option value="employed_part">Salarié temps partiel</option>
                    <option value="cdd">Salarié en CDD</option>
                    <option value="other">Autre</option>
                </select>
            </div>

            <div v-if="eligibilityForm.status.value === 'unemployed'" class="form-field">
                <label for="ft_inscription_date">
                    <span>Date d'inscription à France Travail&nbsp;<span class="important">*</span></span>
                </label>
                <input type="date" id="ft_inscription_date" v-model="eligibilityForm.ft_inscription_date.value" :class="{ 'input-error': showError('ft_inscription_date') }" @blur="touch('ft_inscription_date')">
            </div>

            <div class="form-field">
                <label for="education_level">
                    <span>Niveau d'études&nbsp;<span class="important">*</span></span>
                </label>
                <select id="education_level" v-model="eligibilityForm.education_level.value" :class="{ 'input-error': showError('education_level') }" @blur="touch('education_level')">
                    <option :value="null" disabled>Choisir un niveau</option>
                    <option value="Niveau 1 : Sans Formation">Niveau 1 : Sans Formation</option>
                    <option value="Niveau 2 : BEPC">Niveau 2 : BEPC</option>
                    <option value="Niveau 3 : CAP, BEP">Niveau 3 : CAP, BEP</option>
                    <option value="Niveau 4 : Baccalauréat">Niveau 4 : Baccalauréat</option>
                    <option value="Niveau 5 : Bac+2 (DEUG, BTS, DUT, DEUST)">Niveau 5 : Bac+2 (DEUG, BTS, DUT, DEUST)</option>
                    <option value="Niveau 6 : Bac+3 (Licence, licence professionnelle)">Niveau 6 : Bac+3 (Licence, licence professionnelle)</option>
                    <option value="Niveau 6 : Bac+4 (Maîtrise, master 1)">Niveau 6 : Bac+4 (Maîtrise, master 1)</option>
                    <option value="Niveau 7 : Bac+5 (Master, diplôme d'études approfondies, diplôme d'études supérieures spécialisées, diplôme d'ingénieur)">Niveau 7 : Bac+5 (Master, diplôme d'études approfondies, diplôme d'études supérieures spécialisées, diplôme d'ingénieur)</option>
                    <option value="Niveau 8 : Bac+8 (Doctorat, HDR)">Niveau 8 : Bac+8 (Doctorat, HDR)</option>
                    <option value="Niveau z : Inconnu pour la personne">Niveau z : Inconnu pour la personne</option>
                </select>
            </div>

            <div class="form-field">
                <label for="housing_status">
                    <span>Situation de logement</span>
                </label>
                <select id="housing_status" v-model="eligibilityForm.housing_status.value">
                    <option :value="null" disabled>Choisir une situation</option>
                    <option value="stable">Logement stable</option>
                    <option value="hosted">Hébergé chez un tiers</option>
                    <option value="precarious">Hébergement précaire</option>
                    <option value="none">Sans hébergement / rue</option>
                </select>
            </div>
        </section>

        <hr>

        <!-- SITUATION FAMILIALE -->
        <section class="grouped-inputs">
            <h3>Situation familiale</h3>

            <div class="form-field">
                <label for="children">
                    <span>Enfants à charge</span>
                </label>
                <input type="number" id="children" min="0" v-model.lazy="eligibilityForm.children.value" :class="{ 'input-error': showError('children') }" @blur="touch('children')">
            </div>

            <div v-if="eligibilityForm.children.value > 0" class="form-field">
                <label for="children_under_12">
                    <span>Dont enfants de moins de 12 ans</span>
                </label>
                <input type="number" id="children_under_12" min="0" :max="eligibilityForm.children.value" v-model.lazy="eligibilityForm.children_under_12.value" :class="{ 'input-error': showError('children_under_12') }" @blur="touch('children_under_12')">
            </div>

            <div v-if="eligibilityForm.children.value > 0" class="form-field">
                <label for="disabled_child">
                    <span>Dont enfants en situation de handicap</span>
                </label>
                <input type="number" id="disabled_child" min="0" :max="eligibilityForm.children.value" v-model.lazy="eligibilityForm.disabled_child.value" :class="{ 'input-error': showError('disabled_child') }" @blur="touch('disabled_child')">
            </div>

            <div v-if="eligibilityForm.children.value > 0" class="form-field">
                <label for="single_parent">
                    <span>Parent isolé</span>
                </label>
                <SwitchButton id="single_parent" style_type="discreet" v-model:checked="eligibilityForm.single_parent.value"></SwitchButton>
            </div>
        </section>

        <hr>

        <!-- HANDICAP -->
        <section class="grouped-inputs">
            <h3>Situation de handicap</h3>

            <div class="form-field">
                <label for="handicap">
                    <span>En situation de handicap</span>
                </label>
                <SwitchButton id="handicap" style_type="discreet" v-model:checked="eligibilityForm.handicap.value"></SwitchButton>
            </div>

            <div v-if="eligibilityForm.handicap.value" class="form-field">
                <label for="handicap_type">
                    <span>Type de reconnaissance&nbsp;<span class="important">*</span></span>
                </label>
                <select id="handicap_type" v-model="eligibilityForm.handicap_type.value" :class="{ 'input-error': showError('handicap_type') }" @blur="touch('handicap_type')">
                    <option :value="null" disabled>Choisir un type</option>
                    <option value="RQTH">RQTH</option>
                    <option value="IPP_10">IPP ≥ 10% (Sécurité Sociale)</option>
                    <option value="pension_invalidite_cat1">Pension d'invalidité 1ère catégorie</option>
                    <option value="carte_invalidite">Carte d'invalidité</option>
                    <option value="AAH">Allocataire AAH</option>
                    <option value="cap_emploi">Inscrit Cap Emploi</option>
                    <option value="pensionnes_guerre">Pensionné de guerre</option>
                </select>
            </div>
        </section>

        <hr>

        <!-- AIDES & DISPOSITIFS -->
        <section class="grouped-inputs">
            <h3>Aides et dispositifs</h3>

            <div class="form-field">
                <label for="rsa">
                    <span>Bénéficiaire du RSA</span>
                </label>
                <SwitchButton id="rsa" style_type="discreet" v-model:checked="eligibilityForm.rsa.value"></SwitchButton>
            </div>

            <div class="form-field">
                <label for="registered_with_local_mission">
                    <span>Inscrit en Mission Locale</span>
                </label>
                <SwitchButton id="registered_with_local_mission" style_type="discreet" v-model:checked="eligibilityForm.registered_with_local_mission.value"></SwitchButton>
            </div>

            <div class="form-field">
                <label for="exiting_iae_program">
                    <span>Sortant d'un dispositif IAE (depuis moins de 6&nbsp;mois)</span>
                </label>
                <SwitchButton id="exiting_iae_program" style_type="discreet" v-model:checked="eligibilityForm.exiting_iae_program.value"></SwitchButton>
            </div>

            <div class="form-field">
                <label for="exiting_ase_program">
                    <span>Sortant de l'ASE</span>
                </label>
                <SwitchButton id="exiting_ase_program" style_type="discreet" v-model:checked="eligibilityForm.exiting_ase_program.value"></SwitchButton>
            </div>

            <div class="form-field">
                <label for="recent_arrival_in_france">
                    <span>Arrivée récente en France (CIR &lt; 24 mois)</span>
                </label>
                <SwitchButton id="recent_arrival_in_france" style_type="discreet" v-model:checked="eligibilityForm.recent_arrival_in_france.value"></SwitchButton>
            </div>

            <div class="form-field">
                <label for="other_benefits">
                    <span>Autres allocations perçues</span>
                </label>
                <select id="other_benefits" v-model="eligibilityForm.other_benefits.value">
                    <option :value="null">Aucune</option>
                    <option value="ASS">ASS — Allocation de solidarité spécifique</option>
                    <option value="AAH">AAH — Allocation adulte handicapé</option>
                </select>
            </div>
        </section>

        <hr>

        <!-- LOCALISATION -->
        <section class="grouped-inputs">
            <h3>Localisation&nbsp;<span class="important">*</span></h3>

            <div class="form-field">
                <div class="address-field">
                    <Sub_AddressFields
                        id="city"
                        ref="addressRef"
                        :has-error="showError('city')"
                        @selected-address="handleAddress($event)"
                        @blur="touch('city')"
                    />

                    <div v-if="eligibilityForm.qpv_resident.value || eligibilityForm.within_buffer.value === true">
                        <p>Zone QPV&nbsp;:&nbsp;{{ eligibilityForm.zone_name.value }}</p>
                        <span class="important">{{ eligibilityForm.qpv_resident.value ? "Résidence en QPV" : "Résidence dans un rayon de 300 m d'un QPV" }}</span>
                    </div>
                </div>
            </div>

            <!-- <label for="zrr_resident">
                <span>Résident en ZRR</span>
                <SwitchButton id="zrr_resident" style_type="discreet" v-model:checked="eligibilityForm.zrr_resident.value"></SwitchButton>
            </label> -->
        </section>

        <hr>

        <!-- PARCOURS ENTREPRENEURIAL -->
        <section class="grouped-inputs">
            <h3>Parcours entrepreneurial</h3>

            <div class="form-field">
                <label for="registered_entrepreneur_leader">
                    <span>Inscrit à PASS Entrepreneur #Leader</span>
                </label>
                <SwitchButton id="registered_entrepreneur_leader" style_type="discreet" v-model:checked="eligibilityForm.registered_entrepreneur_leader.value"></SwitchButton>
            </div>

            <div v-if="eligibilityForm.registered_entrepreneur_leader.value" class="form-field">
                <label for="nb_el_meetings">
                    <span>Nombre de RDV E#L réalisés&nbsp;<span class="important">*</span></span>
                </label>
                <input type="number" id="nb_el_meetings" min="0" v-model.lazy="eligibilityForm.nb_el_meetings.value" :class="{ 'input-error': showError('nb_el_meetings') }" @blur="touch('nb_el_meetings')">
            </div>
        </section>

        <hr>

        <div class="grouped-btn">
            <button class="primary-button" v-if="!props.loading">Vérifier l'éligibilité</button>
            <button class="secondary-button-discret" v-if="!props.loading" @click.prevent="resetForm">Réinitialiser</button>
        </div>

        <!-- LOADER -->
        <div class="align-center" v-if="props.loading">
            <p>Analyse en cours...</p>
            <LoadingBar :duration="30"></LoadingBar>
        </div>
    </form>
</template>

<script setup>
import SwitchButton         from '@/components/SwitchButton.vue'
import Sub_AddressFields    from './Sub_AddressFields.vue'
import LoadingBar           from '@/components/LoadingBar.vue'
import apiClient            from '@/api.js'
import { reactive, ref, watch, nextTick, computed } from 'vue'

const submitted     = ref(false)
const touched       = reactive({})
const lastEmitted   = ref({})
const addressRef    = ref(null)   // exposes prefill() and reset()

const props = defineProps({
    loading: { type: Boolean, default: false },
    prefill: { type: Object,  default: null  },
})

const emits = defineEmits(['formData', 'resetForm'])

// ── Age calculation ───────────────────────────────────────────────────────────

const computedAge = computed(() => {
    if (!eligibilityForm.birthday.value) return null

    const today       = new Date()
    const dateOfBirth = new Date(eligibilityForm.birthday.value)
    let age = today.getFullYear() - dateOfBirth.getFullYear()

    const hasBirthdayPassed =
        today.getMonth() > dateOfBirth.getMonth() ||
        (today.getMonth() === dateOfBirth.getMonth() && today.getDate() >= dateOfBirth.getDate())

    if (!hasBirthdayPassed) age--

    return Math.max(0, age)
})

// ── Validation helpers ────────────────────────────────────────────────────────

function field(value, ...rules)                   { return { value, rules } }
function required(message = 'Champ requis')       { return { test: (v) => !!v, message } }
function requiredNumber(message = 'Champ requis') { return { test: v => v !== null && v !== '', message } }
function when(condition, message = 'Champ requis') {
    return { test: (v, form) => !condition(form) || (v !== null && v !== ''), message }
}

function showError(fieldName) {
    return !!errors[fieldName] && (submitted.value || !!touched[fieldName])
}

function touch(fieldName) {
    touched[fieldName] = true
}

// ── Errors ────────────────────────────────────────────────────────────────────

const errors = reactive({
    registered: null, registration_date: null, is_executive: null,
    legal_status: null, annual_revenue: null,
    birthday: null, gender: null, status: null, ft_inscription_date: null,
    education_level: null, housing_status: null,
    children: null, children_under_12: null, disabled_child: null, single_parent: null,
    handicap: null, handicap_type: null,
    rsa: null, other_benefits: null, exiting_iae_program: null,
    exiting_ase_program: null, registered_with_local_mission: null,
    recent_arrival_in_france: null,
    idf_resident: null, city: null,
    registered_entrepreneur_leader: null, nb_el_meetings: null,
})

// ── Form definition ───────────────────────────────────────────────────────────

function defaultForm() {
    return {
        registered:                     field(false),
        registration_date:              field(null, when(form => form.registered.value)),
        is_executive:                   field(false),
        legal_status:                   field(null, when(form => form.registered.value)),
        annual_revenue:                 field(0,    requiredNumber()),
        birthday:                       field(null, required()),
        gender:                         field(null, required()),
        status:                         field(null, required()),
        ft_inscription_date:            field(null, when(form => form.status.value === 'unemployed')),
        education_level:                field(null, required()),
        housing_status:                 field(null),
        children:                       field(0,    requiredNumber()),
        children_under_12:              field(0,    requiredNumber()),
        disabled_child:                 field(0,    requiredNumber()),
        single_parent:                  field(false),
        handicap:                       field(false),
        handicap_type:                  field(null, when(form => form.handicap.value)),
        rsa:                            field(false),
        other_benefits:                 field(null),
        exiting_iae_program:            field(false),
        exiting_ase_program:            field(false),
        registered_with_local_mission:  field(false),
        recent_arrival_in_france:       field(false),
        idf_resident:                   field(false),
        city:                           field(null, required()),
        qpv_resident:                   field(false),
        within_buffer:                  field(false),
        zone_name:                      field(null),
        zone_code:                      field(null),
        registered_entrepreneur_leader: field(false),
        nb_el_meetings:                 field(0, when(form => form.registered_entrepreneur_leader.value)),
    }
}

const eligibilityForm = reactive(defaultForm())

// ── Reset ─────────────────────────────────────────────────────────────────────

function resetForm() {
    Object.assign(eligibilityForm, defaultForm())
    submitted.value   = false
    lastEmitted.value = {}
    Object.keys(touched).forEach(key => delete touched[key])

    addressRef.value?.reset()

    emits('resetForm')
}

// ── Validation ────────────────────────────────────────────────────────────────

function formValidation() {
    for (const [fieldName, field] of Object.entries(eligibilityForm)) {
        if (!field.rules?.length) { errors[fieldName] = null; continue }
        const failing     = field.rules.find(rule => !rule.test(field.value, eligibilityForm))
        errors[fieldName] = failing?.message ?? null
    }
    return Object.values(errors).every(e => !e)
}

async function scrollToFirstError() {
    await nextTick()
    const firstErrorField = Object.keys(errors).find(key => errors[key])
    if (!firstErrorField) return

    // 'city' maps to the first inner input of Sub_AddressFields
    const targetId = firstErrorField === 'city' ? 'city-street' : firstErrorField
    const element  = document.getElementById(targetId)
    if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        element.focus?.()
    }
}

// ── Submit ────────────────────────────────────────────────────────────────────

async function emitFormData() {
    isIDF()
    submitted.value = true

    if (!formValidation()) {
        await scrollToFirstError()
        return
    }

    const formValues = Object.fromEntries(
        Object.entries(eligibilityForm).map(([key, field]) => [key, field.value])
    )

    if (lastEmitted.value) {
        formValues.postcode     = lastEmitted.value.postcode    ?? null
        formValues.lat          = lastEmitted.value.lat         ?? null
        formValues.lon          = lastEmitted.value.lon         ?? null
        formValues.street       = lastEmitted.value.street      ?? null
        formValues.housenumber  = lastEmitted.value.housenumber ?? null
    }

    if (formValues.birthday) {
        formValues.age = computedAge.value
    }

    if (formValues.ft_inscription_date) {
        const today  = new Date()
        const ftDate = new Date(formValues.ft_inscription_date)
        const months =
            (today.getFullYear() - ftDate.getFullYear()) * 12 +
            (today.getMonth()    - ftDate.getMonth())
        formValues.unemployment_duration = Math.max(0, months)
    } else {
        formValues.unemployment_duration = null
    }

    formValues.france_travail =
        formValues.status === 'unemployed' && !!formValues.ft_inscription_date

    delete formValues.birthday
    delete formValues.ft_inscription_date

    emits('formData', formValues)
}

function isIDF() {
    eligibilityForm.idf_resident.value =
        lastEmitted.value?.region === 'Île-de-France'
}

// ── Address handler ───────────────────────────────────────────────────────────

async function handleAddress(event) {

    lastEmitted.value = event

    eligibilityForm.city.value = event?.city ?? null

    if (!event) {
        eligibilityForm.qpv_resident.value = null
        eligibilityForm.within_buffer.value = null
        eligibilityForm.zone_name.value = null
        eligibilityForm.zone_code.value = null

        return
    }

    touch('city')

    if (submitted.value) {
        formValidation()
    }

    try {
        const { data } = await apiClient.post('/check_qpv_view/', {
            street: event.street,
            housenumber: event.housenumber,
            city: event.city,
            lat: event.lat,
            lon: event.lon,
        })

        eligibilityForm.qpv_resident.value = data.qpv_resident
        eligibilityForm.within_buffer.value = data.within_buffer
        eligibilityForm.zone_name.value = data.zone_name
        eligibilityForm.zone_code.value = data.zone_code

    } catch (err) {
        console.error('QPV check failed:', err)
    }
}

// ── Watchers ──────────────────────────────────────────────────────────────────

watch(() => eligibilityForm.children.value, (newVal) => {
    if (eligibilityForm.children_under_12.value > newVal)
        eligibilityForm.children_under_12.value = newVal
    if (eligibilityForm.disabled_child.value > newVal)
        eligibilityForm.disabled_child.value = newVal
})

watch(() => eligibilityForm, () => {
    if (!eligibilityForm.registered.value) {
        eligibilityForm.registration_date.value = null
        eligibilityForm.is_executive.value      = false
        eligibilityForm.legal_status.value      = null
        eligibilityForm.annual_revenue.value    = 0
    }
    if (eligibilityForm.status.value !== 'unemployed')
        eligibilityForm.ft_inscription_date.value = null
    if (eligibilityForm.children.value <= 0)
        eligibilityForm.single_parent.value = false
    if (!eligibilityForm.handicap.value)
        eligibilityForm.handicap_type.value = null
    if (!eligibilityForm.registered_entrepreneur_leader.value)
        eligibilityForm.nb_el_meetings.value = 0

    if (submitted.value) formValidation()
}, { deep: true })

// ── Prefill from parent prop ──────────────────────────────────────────────────

watch(() => props.prefill, (porteur) => {
    if (!porteur) return

    resetForm()

    const hasValue = (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== '' &&
        String(value).trim().toLowerCase() !== '(pas de valeur)'

    const parseDate = (value) => {
        if (!hasValue(value)) return null
        const parts = String(value).split('/')
        if (parts.length !== 3) return null
        const [d, m, y] = parts
        return `${y}-${m}-${d}`
    }

    const data = Object.fromEntries(
        Object.entries(porteur).map(([key, value]) => [key, hasValue(value) ? value : null])
    )

    console.log(data)

    if (data['Civilité']) {
        const c = data['Civilité'].toLowerCase()
        eligibilityForm.gender.value =
            c.includes('madame') ? 'female' : c.includes('monsieur') ? 'male' : 'other'
    }

    const birthday = parseDate(data['Datedenaissance'])
    if (birthday) eligibilityForm.birthday.value = birthday

    if (data['Statutemploi']) {
        const s = data['Statutemploi'].toLowerCase()
        if      (s.includes('demandeur'))     eligibilityForm.status.value = 'unemployed'
        else if (s.includes('temps plein'))   eligibilityForm.status.value = 'employed_full'
        else if (s.includes('temps partiel')) eligibilityForm.status.value = 'employed_part'
        else if (s.includes('cdd'))           eligibilityForm.status.value = 'cdd'
        else                                  eligibilityForm.status.value = 'other'
    }

    const ft_inscription_date = parseDate(data['DateinscriptionPôleEmploi'])
    if (ft_inscription_date) eligibilityForm.ft_inscription_date.value = ft_inscription_date

    if (data['Niveaudeformation']) {
        const n = data['Niveaudeformation'].toLowerCase()
        if      (n.includes('niveau 1') || n.includes('sans'))
            eligibilityForm.education_level.value = 'Niveau 1 : Sans Formation'
        else if (n.includes('niveau 2') || n.includes('bepc'))
            eligibilityForm.education_level.value = 'Niveau 2 : BEPC'
        else if (n.includes('niveau 3') || n.includes('cap') || n.includes('bep'))
            eligibilityForm.education_level.value = 'Niveau 3 : CAP, BEP'
        else if (n.includes('niveau 4') || n.includes('baccalauréat') || n.includes('bac pro'))
            eligibilityForm.education_level.value = 'Niveau 4 : Baccalauréat'
        else if (n.includes('niveau 5') || n.includes('bac+2'))
            eligibilityForm.education_level.value = 'Niveau 5 : Bac+2 (DEUG, BTS, DUT, DEUST)'
        else if (n.includes('niveau 6') && (n.includes('bac+3') || n.includes('licence')))
            eligibilityForm.education_level.value = 'Niveau 6 : Bac+3 (Licence, licence professionnelle)'
        else if (n.includes('niveau 6') && (n.includes('bac+4') || n.includes('ma')))
            eligibilityForm.education_level.value = 'Niveau 6 : Bac+4 (Maîtrise, master 1)'
        else if (n.includes('niveau 7') || n.includes('bac+5'))
            eligibilityForm.education_level.value = "Niveau 7 : Bac+5 (Master, diplôme d'études approfondies, diplôme d'études supérieures spécialisées, diplôme d'ingénieur)"
        else if (n.includes('niveau 8') || n.includes('doctorat'))
            eligibilityForm.education_level.value = 'Niveau 8 : Bac+8 (Doctorat, HDR)'
        else
            eligibilityForm.education_level.value = 'Niveau z : Inconnu pour la personne'
    }

    if (data['Formejuridique']) {
        eligibilityForm.registered.value = true
        const f = data['Formejuridique'].toLowerCase()
        eligibilityForm.legal_status.value =
            (f.includes('micro') || f.includes('auto')) ? 'micro' : 'other'
    }

    const registrationDate = parseDate(data['Datecréation'])
    if (registrationDate) eligibilityForm.registration_date.value = registrationDate

    if (data['Travailleurhandicapé?'])
        eligibilityForm.handicap.value = data['Travailleurhandicapé?'].toUpperCase() !== 'FAUX'

    if (data['RSA'])
        eligibilityForm.rsa.value = data['RSA'].toUpperCase() !== 'FAUX'

    if (data['PrescripteurOriginedubnficiairePhase3']) {
        const p = data['PrescripteurOriginedubnficiairePhase3'].toLowerCase()
        if (p.includes('e#l') || p.includes('leader'))
            eligibilityForm.registered_entrepreneur_leader.value = true
    }

    // Prefill the 3-field address component; build the same full string the old
    // single-input used so the API can resolve it to the right municipality.
    if (addressRef.value) {
        const addressParts = [
            data['Adresseligne1'],
            data['Codepostal'],
            data['Ville'],
        ].filter(Boolean).join(' ')

        if (addressParts) addressRef.value.prefill(addressParts)
    }

}, { immediate: false })
</script>

<style lang="scss" scoped>
html label, body label {
    display: inline-block;
}

#eligibility-form {
    border: 2px solid $platinium;
    border-radius: 5px;
    padding: 2rem;

    h2 {
        text-align: center;
    }

    h2,
    h3 {
        color: $dark-blue;
        text-transform: uppercase;
    }

    hr {
        margin-block: 2rem;
        border-color: $platinium;
    }

    label {
        cursor: pointer;
    }

    .form-field {
        display: flex;
        justify-content: space-between;
        gap: 6rem;

        input,
        select,
        .input-wrapper {
            width: 500px;
            box-sizing: border-box;
        }

        .address-field {
            width: 600px;
            margin-inline: auto;
        }
    }

    .input-wrapper {
        display: flex;
        width: 100%;

        &:has(.input-error) {
            outline: 2px solid $darkred;
            border-radius: 5px;
        }

        input {
            flex: 1;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;

            &:focus {
                outline: none;
            }
        }

        .input-suffix {
            display: flex;
            align-items: center;
            padding: 0 10px;
            background-color: $drop-shadow;
            border-top-right-radius: 5px;
            border-bottom-right-radius: 5px;
        }
    }

    .grouped-inputs {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        h3 {
            margin: 0;
        }
    }

    input,
    select {
        &.input-error {
            outline: 2px solid $darkred;

            &:focus {
                outline-color: $darkred;
            }
        }
    }

    :deep(.address-fields input.input-error) {
        outline: 2px solid $darkred;

        &:focus {
            outline-color: $darkred;
        }
    }

    .important {
        color: $darkred;
    }

    button {
        display: flex;
        justify-self: center;
    }

    .align-center {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .grouped-btn {
        display: flex;
        justify-content: center;
        gap: 1rem;
    }
}
</style>